import PageData from '../../PageData';
import Modal from './Modal';
import AdFormService from '../../services/AdFormService';
import ContactForm from '../../forms/ContactForm';
import {surveyOnDuplicatedLeadOn} from '../../services/DuplicatedLeadSurveyService';
import UserContactDataStorageService from '../../services/UserContactDataStorageService';
import {Device} from '../../types/Device';
import {Brand} from '../../types/Brand';
import {Section} from '../../services/Section';
import {PageViewType} from '../../types/PageViewType';
import FormValidations from "../../forms/validator/FormValidations";
import GA4Tracking from "../../core/tracking/GA4Tracking";
import UserExits from "../UserExits";
import {ContactFormTriggers} from "../../forms/ContactFormTriggers";
import {PermissionSendCommunicationsTracking} from "../../core/tracking/PermissionSendCommunicationsTracking";
import {Operations} from "../../forms/Operations";

export default class ContactModal {
  private readonly modal: Modal;

  private readonly contactForm: ContactForm;

  private readonly pageData: PageData;

  private readonly nameInput: HTMLElement;

  private readonly compressedName: HTMLElement | null;

  private readonly emailInput: HTMLElement;

  private readonly compressedEmail: HTMLElement | null;

  private readonly phoneInput: HTMLElement;

  private readonly compressedPhone: HTMLElement | null;

  private readonly compressedForm: HTMLElement | null;

  private readonly openExpandedFormButton: HTMLElement | null;

  private readonly expandedForm: HTMLElement | null;

  private triggeredElementId = '';

  private readonly originalPageDataId: string = '';

  private readonly nestoriaMobilePublisher: HTMLElement | null;

  private isModalTriggeredFromUnBoostedButton: boolean = false;

  private static triggersEventsId: string[] = [];

  private readonly allowMultipleSubmit: boolean;

  private readonly permissionSendCommunications?: PermissionSendCommunicationsTracking;

  constructor(params: {
    modal: Modal,
    contactForm: ContactForm,
    pageData: PageData,
    allowMultipleSubmit?: boolean
  }) {
    this.modal = params.modal;
    this.contactForm = params.contactForm;
    this.pageData = params.pageData;
    this.originalPageDataId = this.pageData.id;
    this.allowMultipleSubmit = params.allowMultipleSubmit || false;
    this.permissionSendCommunications = new PermissionSendCommunicationsTracking();

    this.nameInput = document.getElementById('form-name-contact')!;
    this.compressedName = document.getElementById('contact-modal-form__compressed__name');
    this.phoneInput = document.getElementById('form-phone-contact')!;
    this.compressedPhone = document.getElementById('contact-modal-form__compressed__phone');
    this.emailInput = document.getElementById('form-email-contact')!;
    this.compressedEmail = document.getElementById('contact-modal-form__compressed__email');
    this.compressedForm = document.getElementById('contact-modal-form__compressed');
    this.openExpandedFormButton = document.getElementById('contact-modal-form__compressed__edit');
    this.expandedForm = document.getElementById('contact-modal-form__expanded');
    this.nestoriaMobilePublisher = document.getElementById('nestoria-project-mobile-publisher');
  }

  static setup = (pageData: PageData, params: {
    allowMultipleSubmit?: boolean,
  } = { allowMultipleSubmit: false }) => {
    if (pageData.brand === Brand.LAMUDI || pageData.brand === Brand.PROPERATI) {
      // @ts-ignore
      window.ga = function(){};
    }
    const openTriggers : Array<HTMLElement> = [];
    const closeTriggers : Array<HTMLElement> = [];

    if (document.getElementById('contact-button') !== null && !this.checkElementExists('contact-button')) {
      this.triggersEventsId.push('contact-button');
      openTriggers.push(document.getElementById('contact-button')!);
      if (pageData.pageViewType !== PageViewType.PROJECT_PAGE_DIRECTORY && pageData.pageViewType !== PageViewType.SERP) {
        new AdFormService().applyImpression(pageData, Section.CONTACT_FORM_MOBILE_MODAL_DISPLAYED, 1);
      }
    }

    if (document.getElementById('contact-button-unboosted') !== null && !this.checkElementExists('contact-button-unboosted')) {
      this.triggersEventsId.push('contact-button-unboosted');
      openTriggers.push(document.getElementById('contact-button-unboosted')!);
    }

    if ((pageData.device === Device.MOBILE && (pageData.brand !== Brand.NESTORIA))
        || (pageData.device === Device.DESKTOP && !pageData.isBoosted)) {
      if (!this.checkElementExists('go-to-form-button')) {
        this.triggersEventsId.push('go-to-form-button');
        openTriggers.push(document.getElementById('go-to-form-button')!);
      }

      Array.from(document.querySelectorAll('[id^="obfuscated_content_email"]')).forEach((element) => {
        if (!this.checkElementExists((<HTMLElement>element).id)) {
          this.triggersEventsId.push((<HTMLElement>element).id);
          openTriggers.push(element as HTMLElement);
        }
      });

      Array.from(document.querySelectorAll('[id^="obfuscated_content_url"]')).forEach((element) => {
        if (!this.checkElementExists((<HTMLElement>element).id)) {
          this.triggersEventsId.push((<HTMLElement>element).id);
          openTriggers.push(element as HTMLElement);
        }
      });
    }
    document.querySelectorAll('button[data-button=\'unit-contact-button\']').forEach((trigger) => {
      if (!this.checkElementExists((<HTMLElement>trigger).id)) {
        this.triggersEventsId.push((<HTMLElement>trigger).id);
        openTriggers.push(<HTMLElement>trigger);
      }
    });
    document.querySelectorAll('.js-snippet-contact').forEach((trigger) => {
      if (!this.checkElementExists((<HTMLElement>trigger).id)) {
        this.triggersEventsId.push((<HTMLElement>trigger).id);
      }
      openTriggers.push(<HTMLElement>trigger);
    });
    this.buildCloseTriggers(closeTriggers);

    if (openTriggers.length > 0) {
      let formContainer = document.getElementById('contact-modal-form');
      const contactModal = new ContactModal({
        modal: new Modal({
          pageData,
          element: document.getElementById('contact-modal')!,
          openTrigger: openTriggers,
          closeTrigger: closeTriggers,
        }),
        contactForm: new ContactForm({
          pageData,
          formContainer: formContainer!,
          nameElement: document.getElementById('form-name-contact')! as HTMLInputElement,
          emailElement: document.getElementById('form-email-contact')! as HTMLInputElement,
          phoneElement: document.getElementById('form-phone-contact')! as HTMLInputElement,
          messageElement: document.getElementById('form-message-contact')! as HTMLTextAreaElement,
          submitElement: document.getElementById('request-info-send-button-contact')!,
          operations: new Operations(formContainer),
        }),
        pageData: pageData,
        allowMultipleSubmit: params.allowMultipleSubmit,
      });
      contactModal.setupEventListeners();
    }
    new ContactFormTriggers(pageData)
  };

  private prefillInputs = () => {
    new UserContactDataStorageService().fillInputsWithStoredValues(
        { input: this.emailInput, type: 'email' },
        { input: this.nameInput, type: 'name' },
        { input: this.phoneInput, type: 'phone' },
    );
  };

  private static buildCloseTriggers(closeTriggers: Array<HTMLElement>) {
    if (document.getElementById('close-contact-modal') !== null) {
      closeTriggers.push(document.getElementById('close-contact-modal')!);
    }
    if (document.getElementById('user-exit-back-to-listing-contact') !== null) {
      closeTriggers.push(document.getElementById('user-exit-back-to-listing-contact')!);
    }
  }

  private static checkElementExists = (id: String) => this.triggersEventsId.filter((element) => element === id).length > 0;

  private setupEventListeners = () => {
    this.modal.onOpen = this.onOpen;
    this.modal.onClose = this.onClose;
    this.contactForm.onSubmit = this.onContactFormSubmit;
    const shouldSendApplyImpression =
        !this.pageData.isBoosted &&
        this.pageData.pageViewType !== PageViewType.PROJECT_PAGE_DIRECTORY &&
        this.pageData.pageViewType !== PageViewType.SERP &&
        this.contactForm.existsForm()
    ;
    if (shouldSendApplyImpression) new AdFormService().applyImpression(this.pageData, Section.SECTION_ADPAGE_BOTTOM_CONTACT_UNBOOSTED, 1);
  };

  private fillCompressedFormValues = () => {
    this.updateInfoForCompressedForm(this.compressedName, (this.nameInput as HTMLInputElement).value);
    this.updateInfoForCompressedForm(this.compressedEmail, (this.emailInput as HTMLInputElement).value);
    this.updateInfoForCompressedForm(this.compressedPhone, (this.phoneInput as HTMLInputElement).value);
  };

  private updateInfoForCompressedForm = (element: HTMLElement | null, value: string) => {
    element!.innerHTML = value ? value : "-";
  }

  private areAllRequiredInputsFilled = () => {
    const nameValue = (this.nameInput as HTMLInputElement).value;
    const emailValue = (this.emailInput as HTMLInputElement).value;
    const phoneValue = (this.phoneInput as HTMLInputElement).value;
    let isValidName = FormValidations.isNotEmpty(nameValue);
    let isValidEmail = FormValidations.emailIsValid(emailValue);
    let isValidPhone = FormValidations.isValidPhone(phoneValue);

    if (this.pageData.onlyFormContact || (this.pageData.country === 'AE' || this.pageData.country === 'TH') || this.pageData.phoneMandatory) {
      isValidPhone = FormValidations.isValidMandatoryPhone(phoneValue);
    }

    if (!this.pageData.emailMandatory) {
      isValidEmail = FormValidations.checkOptionalInput(emailValue, FormValidations.emailIsValid);
    }

    return isValidName && isValidEmail && isValidPhone;
  };

  private hideExpandedOrCompressedForms = () => {
    if (this.areAllRequiredInputsFilled()) {
      this.compressedForm!.style.display = "block";
      this.expandedForm!.style.display = "none";
      this.fillCompressedFormValues();
    } else {
      this.compressedForm!.style.display = "none";
    }
  };

  private configureOneClickApplyModal = () => {
    this.hideExpandedOrCompressedForms();
    this.openExpandedFormButton!.addEventListener("click", () => {
      this.compressedForm!.style.display = "none";
      this.expandedForm!.style.display = "block";
    });
  };

  private onOpen = (id: string | undefined, element?: HTMLElement) => {
    this.triggeredElementId = id!;
    if (this.pageData.pageViewType === PageViewType.PROJECT_PAGE_DIRECTORY) {
      this.pageData.id = this.triggeredElementId;
      if (document.getElementById(this.pageData.id)!.dataset.onlycontactform) {
        this.pageData.onlyFormContact = true;
      }
    }
    const adFormService = new AdFormService();
    if (this.triggeredElementId === 'contact-button') {
      adFormService.applyImpression(this.pageData, Section.CONTACT_FORM_MOBILE_MODAL_DISPLAYED, 2);
      this.isModalTriggeredFromUnBoostedButton = false;
    } else if (this.triggeredElementId === 'contact-button-unboosted') {
      adFormService.applyImpression(this.pageData, Section.SECTION_ADPAGE_BOTTOM_CONTACT_UNBOOSTED, 2);
      this.isModalTriggeredFromUnBoostedButton = true;
    } else if (this.triggeredElementId === 'go-to-form-button') {
      adFormService.applyImpression(this.pageData, Section.SECTION_ADPAGE_IMAGE_CAROUSEL_CONTACT, 2);
    } else if (this.triggeredElementId.includes('obfuscated_content_email_') || this.triggeredElementId.includes('obfuscated_content_url_')) {
      adFormService.applyImpression(this.pageData, Section.SECTION_ADPAGE_DESCRIPTION_INFO, 2);
    } else if (this.triggeredElementId === 'serp-contact-button') {
      this.pageData.id = element?.closest(".js-snippet")?.getAttribute("data-idanuncio") || "";
      adFormService.applyImpression(this.pageData, Section.SECTION_SEARCH_AD_CONTACT_FORM, 2);
    } else {
      adFormService.applyImpression(this.pageData, Section.SECTION_ADPAGE_PROJECTS_MODEL_CONTACT, 1);
      this.pageData.id = this.originalPageDataId;
    }

    UserExits.sendMultiEnquiryImpressions(
        this.pageData,
        this.contactForm.userExitsElement()
    );

    UserExits.sendPostEnquirySimilarsImpressions(
        this.pageData,
        this.contactForm.userExitsElement()
    );

    this.prefillInputs();

    if (this.pageData.brand === Brand.MITULA) {
      this.configureOneClickApplyModal();
    }
  };

  private onClose = () => {
    this.resetFormErrors();
    if (this.allowMultipleSubmit) {
      this.contactForm.resetSubmittedForm();
    }
  };

  private removeError = (input: HTMLElement) => {
    if (input) input.classList.remove('invalid');
  };

  private resetFormErrors = () => {
    this.removeError(this.nameInput);
    this.removeError(this.emailInput);
    this.removeError(this.phoneInput);
  };

  private onContactFormSubmit = () => {
    if (this.nestoriaMobilePublisher) this.nestoriaMobilePublisher.style.display = 'none';

    this.theLeadHasBeenSent();
    if (this.pageData.pageViewType === PageViewType.PROJECT_PAGE_DIRECTORY) {
      this.pageData.id = this.triggeredElementId;
    }
    surveyOnDuplicatedLeadOn(this.pageData, 'contact_modal');

    let section = this.getSectionForLead();

    if (this.isModalTriggeredFromUnBoostedButton) section = Section.SECTION_ADPAGE_BOTTOM_CONTACT_UNBOOSTED;

    let contactModalElement = document.getElementById('contact-modal-form')!;
    if (this.triggeredElementId !== '') {
      let permissionSendCommunicationsValue: string | null | undefined = null;
        if (this.permissionSendCommunications) {
          permissionSendCommunicationsValue = this.permissionSendCommunications.getPermissionSendCommunications(contactModalElement)
      }

      new AdFormService().applyContactFormSent(
        this.pageData,
        this.contactForm.nameValue(),
        this.contactForm.emailValue(),
        this.contactForm.phoneValue(),
        this.contactForm.messageValue(),
        this.triggeredElementId === 'go-to-form-button' ? 'SECTION_ADPAGE_IMAGE_CAROUSEL_CONTACT' : section,
        permissionSendCommunicationsValue === undefined ? null : permissionSendCommunicationsValue,
      );
    }
    this.trackGA4Events();
    if (this.permissionSendCommunications) {
      this.permissionSendCommunications.emitPermissionSendCommunicationsEvent(this.pageData, contactModalElement);
    }

    const storageService = new UserContactDataStorageService();
    storageService.storeUserInputValue(this.nameInput, 'name');
    storageService.storeUserInputValue(this.emailInput, 'email');
    storageService.storeUserInputValue(this.phoneInput, 'phone');

    storageService.fillInputsWithStoredValues(
      { input: document.getElementById('form-email')!, type: 'email' },
      { input: document.getElementById('form-name')!, type: 'name' },
      { input: document.getElementById('form-phone')!, type: 'phone' },
    );
    this.pageData.id = this.originalPageDataId;
  };

  private trackGA4Events() {
    const mnnGA4Brands: String[] = [Brand.MITULA, Brand.NESTORIA, Brand.NUROA];
    if (mnnGA4Brands.includes(this.pageData.brand)) {
      GA4Tracking.trackEvent("detail-page-apply-modal-form");
    }
  }

  private getSectionForLead = () => {
    let section;
    if (this.triggeredElementId.includes('obfuscated_content_email') || this.triggeredElementId.includes('obfuscated_content_url')) {
      section = 'SECTION_ADPAGE_DESCRIPTION_INFO';
    } else if (this.triggeredElementId === 'contact-button') {
      section = 'CONTACT_FORM_MOBILE_MODAL_DISPLAYED';
    } else if (this.triggeredElementId === 'serp-contact-button') {
      section = Section.SECTION_SEARCH_AD_CONTACT_FORM;
    } else {
      section = 'SECTION_ADPAGE_PROJECTS_MODEL_CONTACT';
    }
    return section;
  };

  private theLeadHasBeenSent = () => {
    const metaALeadHasBeenSent = document.querySelector('meta[name="aLeadHasBeenSent"]') as HTMLMetaElement;
    if (metaALeadHasBeenSent) {
      metaALeadHasBeenSent.content = 'true';
    }
  };
}
